<template>
  <div class="item" v-bind:class="{'wide-view': wideView, 'shared': podcast.shared}">
    <button v-if="!displayPlayerInline" class="open-futuri-media" id="show-futuri-player-{{index}}" v-on:click.prevent="openMedia">Open</button>
    <input v-if="displayPlayerInline"
           type="radio"
           name="play-futuri-media"
           class="play-futuri-media"
           id="show-futuri-player-{{index}}"
           v-on:click="playMedia"
           v-el:activator />
    <label class="show-futuri-player" for="show-futuri-player-{{index}}">
      <div class="media-player-container">
        <img class="item-image" v-if="podcast.image" v-bind:src="podcast.image" v-on:load="imageLoaded">
        <div v-else class="item-image item-image-placeholder"></div>
        <media-player
          v-if="mediaReady"
          v-bind:index="index"
          v-bind:id="podcast.id"
          v-bind:station="podcast.station"
          v-bind:duration="duration"
          v-bind:drop-to-live="dropToLive"
          v-bind:is-playing="isPlaying"
          v-bind:seek-position="seekPos"
          v-bind:time-left="timeLeft"
          v-bind:playing-live-stream="canPlayLiveStream"
          v-on:play="playMedia"
          v-on:pause="pauseMedia"
          v-on:seeking="onSeeking"
        ></media-player>
        <audio v-bind:id="`audio-player-${index}`"
               class="video-player"
               v-el:player
               v-on:timeupdate="onTimeUpdate"
               v-on:canplay="onCanPlay"
               v-on:play="onPlay"
               v-on:pause="onPause"
               v-on:ended="onEnded"
        >
          <source :src="audioSrc" />
        </audio>
      </div>
      <h2>{{podcast.name}}</h2>
      <p class="description">{{podcast.description}}</p>
    </label>
    <div v-el:sharing
      class="addthis_inline_share_toolbox"
      v-bind:data-url="addThisUrl"
      v-bind:data-title="podcast.name"
      v-bind:data-description="podcast.description"
      v-bind:data-media="shareImage"
    ></div>
    <ul v-if="podcast.categories.length > 0" class="categories">
      <li v-for="category in podcast.categories">
        <a v-on:click.prevent="clickCategory(category)">{{category.name}}</a>
      </li>
    </ul>
  </div>
</template>

<script>
import addthis from '../addthis';
import MediaPlayer from './media_player';
import { getLiveStreamUrl } from '../utils/utils';

const urlPrefix = process.env.NODE_ENV === 'production' ? `//${location.host}` : '//post.futurimedia.com';

export default {
  components: {
    'media-player': MediaPlayer,
  },
  props: [
    'displayPlayerInline',
    'dropToLive',
    'containerWidth',
    'index',
    'podcast',
    'smc',
    'useM4a',
  ],
  data() {
    return {
      isPlaying: false,
      seekPos: 0,
      duration: 0,
      timeLeft: 0,
      seekTime: null,
      mediaReady: false,
      canPlayLiveStream: false,
      liveStreamUri: '',
      preventAudioSrc: true,
    };
  },
  methods: {
    openMedia() {
      window.open(this.playerUrl, '_blank', 'status=yes, resizable=yes');
    },

    /**
     * Play this media.
     */
    playMedia() {
      this.preventAudioSrc = false;
      this.pauseOthers(this.index);
      const audioPlayer = this.$els.player;
      if (this.canPlayLiveStream && !this.isPlaying) {
        // if live stream paused and play is clicked, original playlist should start again
        this.canPlayLiveStream = false;
      }

      this.updatePlayerElementIfNecessary();

      // Now is this one already playing?
      if (this.isPlaying) {
        this.pauseMedia();
      } else {
        audioPlayer.play()
          // eslint-disable-next-line no-console
          .catch(err => { console.error('Play failed', err); });
      }
    },

    updatePlayerElementIfNecessary() {
      const audioPlayer = this.$els.player;
      if (this.useM4a) {
        // Viblast doesn't support m4a playback, so we want to skip its setup and use native support
        if (!audioPlayer.src || (!this.canPlayLiveStream && audioPlayer.src && !audioPlayer.src.includes('m4a'))) {
          window.viblast(audioPlayer).stop(); // detatch viblast
          audioPlayer.setAttribute('src', this.audioSrc);
        }
      } else {
        const audioElSrc = audioPlayer.dataset.viblastSrc && audioPlayer.dataset.viblastSrc.split(':').pop(); // removes protocol
        if (!audioPlayer.viblast || (!this.canPlayLiveStream && audioElSrc !== this.audioSrc)) {
          this.setupViblast();
        }
      }
    },

    /**
     * Pause this media in particular.
     */
    pauseMedia() {
      if (this.$els.player) {
        this.$els.player.pause();
      }
    },

    /**
     * Pause all other players except for this one.
     */
    pauseOthers() {
      this.$emit('pauseothers');
    },

    /**
     * Event emitted when seeking through audio.
     * @param pos
     */
    onSeeking(pos) {
      // Update the video time
      this.$els.player.pause();
      this.seekTime = this.$els.player.duration * (pos / 100);
      this.$els.player.currentTime = this.seekTime;
      this.$els.player.play().catch(() => {
        // Error is being thrown because pause is interrupting the play at times
      });
    },

    clickCategory(cat) {
      this.$parent.switchCategory(cat);
    },

    imageLoaded() {
      this.$parent.imageLoaded();
    },

    playLiveStream() {
      getLiveStreamUrl(this.podcast.station)
        .then((url) => {
          this.liveStreamUri = url;
          this.canPlayLiveStream = true;
          this.setupViblast();
          this.$els.player.play()
            // eslint-disable-next-line no-console
            .catch(err => { console.warn('Play call interrupted', err); });
        })
        // eslint-disable-next-line no-console
        .catch(e => { console.warn('Unable to fetch live stream', e); });
    },

    setupViblast() {
      try {
        const audioPlayer = this.$els.player;
        window.viblast(audioPlayer).stop(); // eslint-disable-line
        window.viblast(audioPlayer).setup({ // eslint-disable-line
          key: 'ebb0b350-fb99-4e69-8337-0b18f080df4a',
          stream: this.audioSrc,
          forceHEAAC: true,
          autoplay: false,
        });
      } catch (e) {
        // This try/catch block is required. Viblast throws an error on iOS browsers
        //   that we ignore so this works.
      }
    },

    onTimeUpdate(event) {
      // Update the slider value to either new seek time or currentTime
      this.seekPos = (100 / (event.target.duration || 1)) * event.target.currentTime;
      this.timeLeft = (event.target.duration || 0) - event.target.currentTime;
    },

    onCanPlay(event) {
      this.duration = event.target.duration;
      this.mediaReady = true;
    },

    onPlay() {
      this.isPlaying = true;
    },

    onPause() {
      this.isPlaying = false;
    },

    onEnded() {
      this.isPlaying = false;
      if (this.dropToLive) {
        this.playLiveStream();
      }
    },
  },
  computed: {
    m3u8URI() {
      return `${this.podcastUrl}/playlist/${this.podcast.id}.m3u8`;
    },
    m4aURI() {
      return `${urlPrefix}${this.podcast.m4aFile}`;
    },
    audioSrc() {
      if (this.preventAudioSrc) {
        return undefined;
      }
      if (!this.canPlayLiveStream) {
        return this.useM4a ? this.m4aURI : this.m3u8URI;
      }
      return this.liveStreamUri;
    },
    isIOS() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    },
    wideView() {
      return this.containerWidth >= 590;
    },
    addThisUrl() {
      if (!this.displayPlayerInline) {
        return this.playerUrl;
      }

      return addthis.url(this.podcast.id, this.podcast.station);
    },
    proto() {
      const proto = document.location.protocol;
      if (proto === 'file:') {
        return 'http:';
      }
      return proto;
    },
    slug() {
      return this.podcast.name.
      toLowerCase().
      replace(/[^a-z0-9]+/g, '-').
      replace(/--+/g, '-').
      replace(/(^-)|(-$)/g, '');
    },
    podcastUrl() {
      const lstation = this.podcast.station ? this.podcast.station.toLowerCase() : '';
      return `${urlPrefix}/${lstation}`;
    },
    playerUrl() {
      return `${this.podcastUrl}/playlist/${this.slug}-${this.podcast.id}.html?smc=${this.smc}`;
    },
    audioUrl() {
      return `${this.podcastUrl}/playlist/dl/${this.podcast.id}.aac`;
    },
    shareImage() {
      const image = this.podcast.shareImage || this.podcast.image;
      if (image.indexOf('http') === 0) {
        return image;
      }
      return `${urlPrefix}${image}`;
    },
  },
  ready() {
    if (this.podcast.shared) {
      // click on the thing
      const event = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true,
      });
      this.$els.activator.dispatchEvent(event);
    }
  },
};
</script>

<style lang="less" scoped>
* {box-sizing: border-box; margin: 0; padding: 0;}

.faded-background(@color) { background: @color; background: linear-gradient(@color, fade(@color, 50%)); }

@keyframes show-player {
  0% { opacity: 0; display: block; bottom: 42px; }
  100% { opacity: 1; bottom: 0;}
}

@keyframes hilight-shared {
  0% { background-color: yellow; }
  100% { background-color: transparent; }
}

.item {
  position: relative;
  padding: 0;
  margin: 0;
  border-top: #dddddd 1px solid;

  &.shared {
    animation: hilight-shared 2s ease 1;
  }

  button.open-futuri-media { display: none; }

  input[type=radio].play-futuri-media { display: none; }

  label.show-futuri-player {
    display: block;
    position: relative;
    font-size: 0;

    .media-player-container {
      border-radius: 10px;
      margin: 24px 0 12px 0;
      overflow: hidden;
      position: relative;

      .item-image {
        width: 100%;
        height: auto;
        max-width: 700px;
        cursor: pointer;
      }

      .item-image-placeholder {
        height: 200px;
        .faded-background(lightgrey);
      }
    }
    h2 {
      font-family: "Helvetica Neue",Helvetica,Arial,sans-serif;
      font-weight: normal;
      font-size: 16px;
      padding: 0;
      margin: 0 0 6px 0;
      cursor: pointer;
    }

    p.description {
      font-family: "Helvetica Neue",Helvetica,Arial,sans-serif;
      font-weight: normal;
      font-size: 12px;
      color: grey;
      margin: 0 0 5px 0;
      padding: 0;
      cursor: pointer;
    }

    &:after {
      content: "";
      position: absolute;
      top: 12px;
      right: 16px;
      display: inline-block;
      width: 30px;
      height: 30px;
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.4);
      cursor: pointer;
      background-image: url(../assets/play-circle.svg);
      background-size: contain;
      border-radius: 15px;
      background-color: rgba(0,0,0,.4)
    }

    &:hover {
      h2 { opacity: .6; }
      p.description { opacity: .6; }
      &:after { opacity: .6; }
    }
  }

  input[type=radio].play-futuri-media:checked + label.show-futuri-player {
    .media-player-container {
      .item-image {  cursor: default; }
      .media-player { display: block; animation: show-player .5s ease 1; }
    }
    &:after { display: none; }
    &:hover {
      h2 { opacity: 1; cursor: default; }
      p.description { opacity: 1; cursor: default; }
      &:after { opacity: 1; cursor: default; }
    }
  }
  ul.categories {
    font-family: "Helvetica Neue",Helvetica,Arial,sans-serif;
    font-weight: normal;
    font-size: 0;
    list-style: none;
    margin: 12px 0 24px 0;
    padding: 0;

    &:before {
      content: "";
      display: inline-block;
      margin-right: 6px;
      background-image: url(../assets/tag.svg);
      background-size: cover;
      height: 12px;
      width: 12px;
    }
    li {
      display: inline-block;
      font-family: "Helvetica Neue",Helvetica,Arial,sans-serif;
      font-weight: normal;
      font-size: 12px;
      a {
        color: grey;
        cursor: pointer;
        &:hover { opacity: .6; }
      }
      &:not(:last-child) {
        &:after {
          content: ", ";
          margin-left: 0px;
        }
      }
    }
  }
  &:first-child { border-top: none;
    label.show-futuri-player {
      .media-player-container { margin-top: 0; }
    }
  }
}

.item.wide-view { // happens when wideView is true
  width: ~"calc(50% - 6px)";
  display: inline-block;
  vertical-align: top;
  margin: 0 12px 0 0;
  &:nth-child(odd) { margin: 0 0 0 0; }
  &:first-child { width: 100%; margin: 0 0 0 0;
    label.show-futuri-player {
      .media-player-container { border-radius: 10px; }
      h2 { font-size: 24px; }
      &:after { top: 24px; right: 24px; width: 60px; height: 60px; border-radius: 30px; }
    }
  }
}
.dark-theme .item {
  label.show-futuri-player {
    h2 {
      color: white;
    }
    p.description {
      color: lightgrey;
    }
  }
  ul.categories li a {
    color: lightgrey;
  }
}

.paginate-transition {
  transition: transform .5s linear;
}

.paginate-enter {
  opacity: 0;
  transform: translate(100%);
}

.paginate-leave {
  height: 0;
  left: -10000px;
  position: absolute;
  transform: translate(-100%);
}

.video-player {
  height: 0;
}
</style>



// WEBPACK FOOTER //
// podcast.vue?f144c1f6